import { Autocomplete } from "@react-google-maps/api";
import queryString from "query-string";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { CloseIcon } from "@chakra-ui/icons";
import { IconButton } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useIntl } from "react-intl";
import { InputText } from "../components/InputText";
import { useConfig } from "../hooks/useConfig";
import { useFocusStyle } from "../hooks/useFocusStyle";
import { onSelectAddressAnalytics } from "../services/analytics";
import { RootState } from "../store";
import { removeFilterField } from "../store/filter";
import { setSortType, SortTypeEnum } from "../store/sort";

interface Props {
  onFocus?: () => void;
  onBlur?: () => void;
  map?: google.maps.Map;
}

export const AddressSearchBar = (props: Props): React.ReactElement | null => {
  const { onFocus, onBlur, map } = props;
  const focusStyle = useFocusStyle({ position: "offset", color: "primary" });

  const [autocomplete, setAutocomplete] =
    React.useState<google.maps.places.Autocomplete>();
  const address = useSelector((state: RootState) => state.address);
  const { setPlaceId } = useSetPlaceId();
  const intl = useIntl();
  const googleMap = useSelector((state: RootState) => state.googleMap);
  const config = useConfig();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [addressError, setAddressError] = React.useState<string | null>(null);
  const placeSelectedTime = React.useRef<number>(-Infinity);

  const currentMarkerAddress =
    address.status === "finished" ? address.values.formattedText : "";

  const handleLoad = (ac: google.maps.places.Autocomplete) => {
    setAutocomplete(ac);
  };

  const handleClear = () => {
    if (inputRef.current?.value) {
      inputRef.current.value = "";
      setPlaceId(null);
      setAddressError(null);
    }
  };

  const handleChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setPlaceId(null);
      setAddressError(null);
    }
  }, 300);

  const handleBlur = () => {
    onBlur?.();

    // Without a call to the Places API (see note below), the "place_changed"
    // event should fire before the blur event, but we'll wait a short delay
    // just in case.
    setTimeout(() => {
      const msecSincePlaceSelection = Date.now() - placeSelectedTime.current;
      if (inputRef.current?.value === currentMarkerAddress) {
        // The user has not changed the address, so clear any existing error.
        setAddressError(null);
      } else if (msecSincePlaceSelection > 500) {
        // The user has changed the address, and not as a result of a recent
        // place selection.
        setAddressError(
          intl.formatMessage({ id: "ADDRESS_ERROR_INVALID_SELECTION" })
        );
      }
    }, 100);
  };

  const handlePlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();

    // Note: Contrary to the type definition, the place object may be undefined.
    if (!place?.place_id) {
      // A lack of place_id mainly occurs if the user presses the Enter key
      // without selecting an address from the dropdown.
      if (inputRef.current?.value !== currentMarkerAddress) {
        setAddressError(
          intl.formatMessage({ id: "ADDRESS_ERROR_INVALID_SELECTION" })
        );
      }
      return;
    }

    placeSelectedTime.current = Date.now();
    setAddressError(null);
    setPlaceId(place.place_id);

    onSelectAddressAnalytics();
  };

  if (address.status === "loading") return null;
  if (googleMap.status === "loading") return null;

  return (
    <Autocomplete
      onLoad={handleLoad}
      onPlaceChanged={handlePlaceChanged}
      bounds={map?.getBounds()}
      // Note: The fields "place_id", "name", "types" do not require a call to
      // the Places API to get the place details. This means that by restricting
      // to these fields, the "place_changed" event will fire immediately after
      // the user selects a suggestion, and in particular before the blur event
      // is fired.
      options={{ fields: ["place_id"] }}
    >
      <InputText
        ref={inputRef}
        label="Address"
        defaultValue={currentMarkerAddress}
        placeholder={intl.formatMessage(
          {
            id: "ADDRESS_PLACEHOLDER",
          },
          { schools: config?.variableWords.school.plural.label.toLowerCase() }
        )}
        inputRightElement={
          <IconButton
            onClick={handleClear}
            variant="ghost"
            aria-label="clear search address"
          >
            <CloseIcon />
          </IconButton>
        }
        onFocus={onFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        backgroundColor="white"
        isInvalid={!!addressError}
        errorMessage={addressError || undefined}
        floatingError
        sx={focusStyle}
      />
    </Autocomplete>
  );
};

function useSetPlaceId() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const setPlaceId = (placeId: string | null) => {
    if (placeId) {
      const params = queryString.parse(location.search);
      params["place_id"] = placeId;
      navigate(`${location.pathname}?${queryString.stringify(params)}`);
    } else {
      dispatch(removeFilterField({ fieldName: "distance" }));
      dispatch(setSortType(SortTypeEnum.ALPHABETICAL));
      navigate(`${location.pathname}`);
    }
  };
  return { setPlaceId };
}
